import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  MenuItem,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DescribedField from '../DescribedField';

const emptyItem = () => ({
  id: crypto.randomUUID(),
  name: '',
  text: '',
  rating: '',
  date: '',
});

/**
 * TestimonialsBlockEditor — Editor do bloco Depoimentos
 * TODO 4.12
 *
 * Props:
 *   - value: { items: [{ id, name, text, rating, date }] }
 *   - onChange: (patch) => void
 *   - context: object
 */
const TestimonialsBlockEditor = ({ value = {}, onChange }) => {
  const { items = [] } = value;

  const patchItems = (newItems) => onChange({ ...value, items: newItems });

  const addItem = () => patchItems([...items, emptyItem()]);

  const removeItem = (index) => patchItems(items.filter((_, i) => i !== index));

  const updateItem = (index, field, val) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [field]: val } : item
    );
    patchItems(updated);
  };

  return (
    <DescribedField
      label="Depoimentos"
      description="Depoimentos de clientes e pacientes que humanizam e convencem visitantes. Construído manualmente — preencha com depoimentos reais. Use nome ou iniciais para respeitar a privacidade. Avaliação e data são opcionais e, se preenchidos, aparecem na página."
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((item, index) => (
          <Paper key={item.id ?? index} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Depoimento {index + 1}</Typography>
              <IconButton
                size="small"
                onClick={() => removeItem(index)}
                aria-label="Remover depoimento"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              fullWidth
              label="Nome"
              value={item.name}
              onChange={(e) => updateItem(index, 'name', e.target.value)}
              size="small"
              sx={{ mb: 1.5 }}
              helperText="Nome ou iniciais (respeitando privacidade do cliente)."
            />
            <TextField
              fullWidth
              label="Depoimento"
              value={item.text}
              onChange={(e) => updateItem(index, 'text', e.target.value)}
              size="small"
              multiline
              rows={3}
              sx={{ mb: 1.5 }}
              helperText="Depoimento na íntegra."
            />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                select
                label="Avaliação (opcional)"
                value={item.rating}
                onChange={(e) => updateItem(index, 'rating', e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                helperText="Estrelas exibidas se preenchido."
              >
                <MenuItem value="">-- Sem avaliação --</MenuItem>
                {[1, 2, 3, 4, 5].map((n) => (
                  <MenuItem key={n} value={n}>
                    {'★'.repeat(n)} ({n})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Data (opcional)"
                type="date"
                value={item.date}
                onChange={(e) => updateItem(index, 'date', e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Paper>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={addItem}
          variant="outlined"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        >
          Adicionar depoimento
        </Button>
      </Box>
    </DescribedField>
  );
};

TestimonialsBlockEditor.propTypes = {
  value: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        text: PropTypes.string,
        rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        date: PropTypes.string,
      })
    ),
  }),
  onChange: PropTypes.func.isRequired,
  context: PropTypes.object,
};

export default TestimonialsBlockEditor;
